import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode
} from "react";
import type { Eip1193Provider } from "./wallet";

const WalletRuntime = lazy(() => import("./WalletRuntime"));

export interface WalletRuntimeSnapshot {
  address: string;
  provider: Eip1193Provider | null;
  chainId: number | null;
  wrongNetwork: boolean;
}

interface WalletState extends WalletRuntimeSnapshot {
  runtimeReady: boolean;
  openPanel(): void;
  disconnect(): Promise<void>;
}

const initialSnapshot: WalletRuntimeSnapshot = {
  address: "",
  provider: null,
  chainId: null,
  wrongNetwork: false
};

const WalletContext = createContext<WalletState | null>(null);

export function WalletProvider({ children }: { children: ReactNode }) {
  const [runtimeEnabled, setRuntimeEnabled] = useState(false);
  const [runtimeReady, setRuntimeReady] = useState(false);
  const [panelRequest, setPanelRequest] = useState(0);
  const [disconnectRequest, setDisconnectRequest] = useState(0);
  const [snapshot, setSnapshot] = useState(initialSnapshot);

  const updateSnapshot = useCallback((next: WalletRuntimeSnapshot) => {
    setSnapshot(next);
    setRuntimeReady(true);
  }, []);

  const value = useMemo<WalletState>(() => ({
    ...snapshot,
    runtimeReady,
    openPanel: () => {
      setRuntimeEnabled(true);
      setPanelRequest((request) => request + 1);
    },
    disconnect: async () => {
      if (!runtimeEnabled) return;
      setDisconnectRequest((request) => request + 1);
    }
  }), [snapshot, runtimeReady, runtimeEnabled]);

  return (
    <WalletContext.Provider value={value}>
      {children}
      {runtimeEnabled && (
        <Suspense fallback={null}>
          <WalletRuntime
            panelRequest={panelRequest}
            disconnectRequest={disconnectRequest}
            onSnapshot={updateSnapshot}
          />
        </Suspense>
      )}
    </WalletContext.Provider>
  );
}

export function useWallet(): WalletState {
  const value = useContext(WalletContext);
  if (!value) throw new Error("useWallet must be used inside WalletProvider");
  return value;
}
